import { MRT_RowData } from "material-react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SelectManyFromFilter } from "src/components/Grid/filters/SelectManyFromFilter/SelectManyFromFilter";
import { SelectOneFromFilter } from "src/components/Grid/filters/SelectOneFromFilter/SelectOneFromFilter";
import type { CustomMRTFilter } from "src/components/Grid/filters/mrt.types";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import {
    NETWORK_QUESTIONNAIRE_STATUSES,
    type NetworkQuestionnaireStatus,
} from "src/api/client-api/network/useNetworkLists";

export type NetworkOption = { value: string; label: string };

const optionApi = {
    getId: (option: NetworkOption) => option.value,
    getName: (option: NetworkOption) => option.label,
    getValue: (option: NetworkOption) => option.value,
};

type NetworkOptionsFilterProps<Row extends MRT_RowData> = {
    mrtProps: CustomMRTFilter<Row>;
    options: NetworkOption[];
};

export const NetworkOptionsFilter = <Row extends MRT_RowData>(props: NetworkOptionsFilterProps<Row>) => {
    const { mrtProps, options } = props;
    const searchApi = useDelayedSearch({ defaultDelay: 0 });

    return (
        <SelectManyFromFilter
            appearance="product-v2"
            mrtProps={mrtProps}
            searchApi={searchApi}
            selectOptions={{
                ...optionApi,
                kind: "checkbox",
                values: options.filter((option) => searchApi.doesSearchMatch(option.label)),
            }}
        />
    );
};

export const useNetworkStatusLabels = (): Record<NetworkQuestionnaireStatus, string> => {
    const { t } = useTranslation();

    return useMemo(
        () => ({
            validated: t("network.status.validated", "Validated"),
            in_progress: t("network.status.in_progress", "In progress"),
            not_started: t("network.status.not_started", "Not started"),
        }),
        [t],
    );
};

export const useNetworkStatusOptions = (): NetworkOption[] => {
    const labels = useNetworkStatusLabels();

    return useMemo(
        () => NETWORK_QUESTIONNAIRE_STATUSES.map((status) => ({ value: status, label: labels[status] })),
        [labels],
    );
};

export const NetworkStatusFilter = <Row extends MRT_RowData>(props: { mrtProps: CustomMRTFilter<Row> }) => {
    const { mrtProps } = props;
    const options = useNetworkStatusOptions();
    const searchApi = useDelayedSearch({ defaultDelay: 0 });

    return (
        <SelectOneFromFilter
            appearance="product-v2"
            mrtProps={mrtProps}
            searchApi={searchApi}
            selectOptions={{
                ...optionApi,
                kind: "radio",
                values: options.filter((option) => searchApi.doesSearchMatch(option.label)),
            }}
        />
    );
};

export const optionLabelsChip = (options: NetworkOption[]) => (filterValue: unknown) => {
    const values =
        Array.isArray(filterValue) ? filterValue.map(String)
        : typeof filterValue === "string" && filterValue !== "" ? [filterValue]
        : [];
    if (values.length === 0) return null;

    const labelByValue = new Map(options.map((option) => [option.value, option.label]));

    return values.map((value) => labelByValue.get(value) ?? value).join(", ");
};
